<script setup>
import { computed, useTemplateRef } from 'vue';
import { useStore } from 'vuex';
import { useElementSize } from '@vueuse/core';
import { REPLY_EDITOR_MODES } from './constants';

const props = defineProps({
  mode: {
    type: String,
    default: REPLY_EDITOR_MODES.REPLY,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

defineEmits(['toggleMode', 'setBotMode']);

const store = useStore();
const currentUser = computed(() => store.getters.getCurrentUser);
const currentChat = computed(() => store.getters.getSelectedChat);
const hasBotLabel = computed(() => {
  const chat = currentChat.value;
  if (!chat?.labels) return false;
  return chat.labels.some(l => {
    const name = typeof l === 'string' ? l : (l.title || l.name || '');
    return name.toLowerCase() === 'qualificacao-em-andamento';
  });
});
const isBotMode = computed(() => props.mode === REPLY_EDITOR_MODES.BOT);
const botIconUrl = '/static/img/sz-logo-ia.svg';

const wootEditorReplyMode = useTemplateRef('wootEditorReplyMode');
const wootEditorPrivateMode = useTemplateRef('wootEditorPrivateMode');
const wootEditorBotMode = useTemplateRef('wootEditorBotMode');
const botModeSize = useElementSize(wootEditorBotMode);

const replyModeSize = useElementSize(wootEditorReplyMode);
const privateModeSize = useElementSize(wootEditorPrivateMode);

const isPrivate = computed(() => {
  return props.disabled || props.mode === REPLY_EDITOR_MODES.NOTE;
});
const botMode = computed(() => props.mode === REPLY_EDITOR_MODES.BOT);

const width = computed(() => {
  let widthToUse;
  if (botMode.value) {
    widthToUse = botModeSize.width.value;
  } else if (isPrivate.value) {
    widthToUse = privateModeSize.width.value;
  } else {
    widthToUse = replyModeSize.width.value;
  }
  const widthWithPadding = widthToUse + 16;
  return `${widthWithPadding}px`;
});

const translateValue = computed(() => {
  let xTranslate = 0;
  if (botMode.value) {
    xTranslate = replyModeSize.width.value + 16 + privateModeSize.width.value + 16;
  } else if (isPrivate.value) {
    xTranslate = replyModeSize.width.value + 16;
  }
  return `${xTranslate}px`;
});
</script>

<template>
  <button
    class="flex items-center w-auto h-8 p-1 transition-all border rounded-full bg-n-alpha-2 group relative duration-300 ease-in-out z-0 active:scale-[0.995] active:duration-75"
    :disabled="disabled"
    :class="{
      'cursor-not-allowed': disabled,
    }"
    :data-sz-mode="botMode ? 'bot' : (isPrivate ? 'note' : 'reply')" @click="$emit('toggleMode')"
  >
    <div ref="wootEditorReplyMode" class="flex items-center gap-1 px-2 z-20">
      <img
        v-if="currentUser.avatar_url"
        :src="currentUser.avatar_url"
        class="sz-reply-avatar"
      />
      {{ $t('CONVERSATION.REPLYBOX.REPLY') }}
    </div>
    <div ref="wootEditorPrivateMode" class="flex items-center gap-1 px-2 z-20">
      {{ $t('CONVERSATION.REPLYBOX.PRIVATE_NOTE') }}
    </div>
    <div
      ref="wootEditorBotMode"
      class="flex items-center gap-1 px-2 z-20 sz-bot-chip"
      :class="{ 'text-n-teal-11': botMode }"
      @click.stop="$emit('setBotMode')"
    >
      <img :src="botIconUrl" class="sz-bot-icon-img" alt="" />
      Agente AI
      <!-- Chevron down -->
      <svg class="sz-bot-chevron" width="10" height="10" viewBox="0 0 24 24" fill="none">
        <path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </div>
    <div
      :class="['absolute shadow-sm rounded-full h-6 w-[var(--chip-width)] ease-in-out translate-x-[var(--translate-x)] rtl:translate-x-[var(--rtl-translate-x)]', botMode ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-n-solid-1', { 'transition-all duration-300': !disabled }]"
      :style="{
        '--chip-width': width,
        '--translate-x': translateValue,
        '--rtl-translate-x': `calc(-1 * var(--translate-x))`,
      }"
    />
  </button>
</template>

<style scoped>
.sz-reply-avatar {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}
.sz-bot-icon-img {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}
</style>
